import { useMemo } from 'react';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { ExternalBatchContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { FormScrollProvider } from '@shared/scenes/entity/context';
import { CardEntityCreateForm } from '@shared/scenes/entity/create/components/CardEntityCreateForm';
import { useCreateCardEntity } from '@shared/scenes/entity/create/hooks/useCreateCardEntity';
import { Colors, grid } from '@shared/styles';
import { useChannel } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useUnsavedChangesNavigationWarning } from '@shared/util/navigation';
import styles from './CreateCardEntityForm.module.css';

export function CreateCardEntityForm({
  channelId,
  templateSlug,
}: {
  channelId: number;
  templateSlug: string;
}) {
  const channel = useChannel({ channelId });
  const workflowTemplateId = channel?.workflowTemplateId;

  return (
    <FormScrollProvider>
      <ExternalBatchContextProvider>
        <Form workflowTemplateId={workflowTemplateId ?? undefined} templateSlug={templateSlug} />
      </ExternalBatchContextProvider>
    </FormScrollProvider>
  );
}

function Form({
  workflowTemplateId,
  templateSlug,
}: {
  workflowTemplateId?: string;
  templateSlug: string;
}) {
  const queryFieldOverrides = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const fields = params.get('fields');
    if (!fields) {
      return undefined;
    }
    try {
      return JSON.parse(fields);
    } catch (error) {
      return undefined;
    }
  }, []);

  const {
    components,
    fields,
    updateField,
    errors,
    isDirty,
    onPressSave,
    status,
    viewTemplateTitle,
    viewTemplate,
    resetForm,
    saveAttempted,
  } = useCreateCardEntity({
    workflowTemplateId,
    viewTemplateId: templateSlug,
    queryFieldOverrides,
  });
  const isSaveSuccessful = status === 'success';
  const showUnsavedChangesWarning = !isSaveSuccessful && isDirty;
  useUnsavedChangesNavigationWarning(showUnsavedChangesWarning);

  if (isSaveSuccessful) {
    return <SaveSuccess resetForm={resetForm} />;
  }

  return (
    <View className={styles.container}>
      <Header title={viewTemplateTitle} showSave={isDirty} onSave={onPressSave} />
      <View className={styles.form}>
        <CardEntityCreateForm
          components={components}
          fields={fields}
          updateField={updateField}
          errors={errors}
          workflowTemplateId={workflowTemplateId}
          viewTemplateId={viewTemplate?.id}
          saveAttempted={saveAttempted}
          isAuthenticated={false}
        />
      </View>
    </View>
  );
}

function Header({
  title,
  showSave,
  onSave,
}: {
  title: string;
  showSave: boolean;
  onSave: () => void;
}) {
  return (
    <View className={styles.header}>
      <View row gap={1} centerVertically className={styles.headerContent}>
        <Text size="h4" numberOfLines={2} flex balance>
          {title}
        </Text>
        {showSave && <Button variant="primary" label={i18n('common.create')} onClick={onSave} />}
      </View>
    </View>
  );
}

function SaveSuccess({ resetForm }: { resetForm: () => void }) {
  return (
    <View className={[styles.container, styles.successContainer]}>
      <View className={styles.successLogo}>
        <MBIconV2 name="coast-symbol" color={Colors.v2.green} size={grid(10)} />
      </View>
      <View className={styles.successText}>
        <Text size="h4" className={styles.successTitle}>
          {i18n('externalForm.success.title')}
        </Text>
        <Text size="body2">{i18n('externalForm.success.description')}</Text>
        <View className={styles.successButton}>
          <Button variant="secondary" label={i18n('common.createAnother')} onClick={resetForm} />
        </View>
      </View>
    </View>
  );
}
